import React, { useEffect, useMemo, useState } from 'react';
import {
  AlertTriangle,
  CalendarDays,
  ClipboardCheck,
  Clock,
  RefreshCw,
  UserCheck,
  UserX,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { Timestamp, doc, getDoc } from 'firebase/firestore';
import { db } from '../../services/firebase';
import { useAttendance, useEmployees } from '../../hooks/useHR';
import type { Attendance, Employee } from '../../types/firestore';
import type { DataTableColumn } from '../../types/ui';
import Button from '../../components/ui/Button';
import DataTable from '../../components/ui/DataTable';
import EmptyState from '../../components/ui/EmptyState';
import Input from '../../components/ui/Input';
import LoadingSpinner from '../../components/ui/LoadingSpinner';
import MetricCard from '../../components/ui/MetricCard';
import Modal from '../../components/ui/Modal';
import PageHeader from '../../components/ui/PageHeader';
import StatusBadge from '../../components/ui/StatusBadge';
import Surface from '../../components/ui/Surface';

type StatusFilter = 'all' | Attendance['status'];

interface AttendanceSettings {
  workStartTime: string;
  lateThreshold: number;
}

const defaultSettings: AttendanceSettings = {
  workStartTime: '08:00',
  lateThreshold: 15,
};

const padDatePart = (value: number) => String(value).padStart(2, '0');

const toDateInput = (date: Date) =>
  `${date.getFullYear()}-${padDatePart(date.getMonth() + 1)}-${padDatePart(
    date.getDate()
  )}`;

const parseDateInput = (value: string, endOfDay = false) => {
  const [year, month, day] = value.split('-').map(Number);
  const parsed = new Date(year, (month || 1) - 1, day || 1);
  parsed.setHours(endOfDay ? 23 : 0, endOfDay ? 59 : 0, endOfDay ? 59 : 0, endOfDay ? 999 : 0);
  return parsed;
};

const parseDateTimeInput = (date: string, time: string) => {
  const parsed = parseDateInput(date);
  const [hours, minutes] = time.split(':').map(Number);
  parsed.setHours(hours || 0, minutes || 0, 0, 0);
  return parsed;
};

const formatTime = (value?: Timestamp) =>
  value
    ? value.toDate().toLocaleTimeString('ar-EG', {
        hour: '2-digit',
        minute: '2-digit',
      })
    : '--:--';

const formatDate = (value: Timestamp) =>
  value.toDate().toLocaleDateString('ar-EG', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });

const formatDuration = (hours = 0) => {
  const totalMinutes = Math.max(0, Math.round(hours * 60));
  const wholeHours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  if (!wholeHours && !minutes) return '0 دقيقة';
  if (!minutes) return `${wholeHours} ساعة`;
  if (!wholeHours) return `${minutes} دقيقة`;
  return `${wholeHours} ساعة ${minutes} دقيقة`;
};

const statusLabels: Record<Attendance['status'], string> = {
  present: 'حاضر',
  absent: 'غائب',
  late: 'متأخر',
  half_day: 'نصف يوم',
  sick_leave: 'إجازة مرضية',
  vacation: 'إجازة',
  authorized_absence: 'غياب بإذن',
};

const statusTone: Record<
  Attendance['status'],
  'success' | 'danger' | 'warning' | 'info' | 'neutral'
> = {
  present: 'success',
  absent: 'danger',
  late: 'warning',
  half_day: 'warning',
  sick_leave: 'info',
  vacation: 'info',
  authorized_absence: 'neutral',
};

const AttendanceManagement: React.FC = () => {
  const { employees, loading: employeesLoading, error, fetchEmployees } =
    useEmployees();
  const {
    organizationAttendance,
    loading: attendanceLoading,
    fetchOrganizationAttendance,
    recordManualAttendance,
  } = useAttendance();
  const [filters, setFilters] = useState({
    startDate: toDateInput(new Date()),
    endDate: toDateInput(new Date()),
    employeeId: 'all',
    department: 'all',
    status: 'all' as StatusFilter,
  });
  const [settings, setSettings] = useState<AttendanceSettings>(defaultSettings);
  const [manualOpen, setManualOpen] = useState(false);
  const [savingManual, setSavingManual] = useState(false);
  const [manualForm, setManualForm] = useState({
    employeeId: '',
    date: toDateInput(new Date()),
    checkIn: '08:00',
    checkOut: '17:00',
    status: 'present' as Attendance['status'],
    notes: '',
  });

  const activeEmployees = useMemo(
    () => employees.filter(employee => employee.isActive),
    [employees]
  );

  const employeeById = useMemo(
    () =>
      new Map<string, Employee>(
        employees.map(employee => [employee.id, employee])
      ),
    [employees]
  );

  const departments = useMemo(
    () =>
      Array.from(
        new Set(employees.map(employee => employee.department).filter(Boolean))
      ).sort((a, b) => a.localeCompare(b, 'ar')),
    [employees]
  );

  const loadAttendance = async () => {
    const startDate = parseDateInput(filters.startDate);
    const endDate = parseDateInput(filters.endDate, true);
    await fetchOrganizationAttendance(activeEmployees, startDate, endDate);
  };

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const settingsDoc = await getDoc(
          doc(db, 'system_settings', 'attendance_settings')
        );
        if (settingsDoc.exists()) {
          const data = settingsDoc.data() as Partial<AttendanceSettings>;
          setSettings({
            workStartTime: data.workStartTime || defaultSettings.workStartTime,
            lateThreshold:
              typeof data.lateThreshold === 'number'
                ? data.lateThreshold
                : defaultSettings.lateThreshold,
          });
        }
      } catch {
        setSettings(defaultSettings);
      }
    };
    void fetchSettings();
  }, []);

  useEffect(() => {
    if (activeEmployees.length) {
      void loadAttendance();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeEmployees.length, filters.startDate, filters.endDate]);

  const filteredRecords = useMemo(
    () =>
      organizationAttendance.filter(record => {
        const employee = employeeById.get(record.employeeId);
        const matchesEmployee =
          filters.employeeId === 'all' || record.employeeId === filters.employeeId;
        const matchesDepartment =
          filters.department === 'all' ||
          employee?.department === filters.department;
        const matchesStatus =
          filters.status === 'all' || record.status === filters.status;
        return matchesEmployee && matchesDepartment && matchesStatus;
      }),
    [employeeById, filters, organizationAttendance]
  );

  const stats = useMemo(() => {
    const present = filteredRecords.filter(record =>
      ['present', 'late'].includes(record.status)
    ).length;
    const absent = filteredRecords.filter(
      record => record.status === 'absent'
    ).length;
    const late = filteredRecords.filter(record => record.status === 'late').length;
    const totalHours = filteredRecords.reduce(
      (sum, record) => sum + (record.totalHours || 0),
      0
    );
    return {
      present,
      absent,
      late,
      rate: filteredRecords.length
        ? Math.round((present / filteredRecords.length) * 100)
        : 0,
      totalHours,
    };
  }, [filteredRecords]);

  const columns = useMemo<DataTableColumn<Attendance>[]>(
    () => [
      {
        key: 'employee',
        header: 'الموظف',
        cell: record => {
          const employee = employeeById.get(record.employeeId);
          return (
            <div className='min-w-0'>
              <p className='truncate font-semibold text-secondary-950 dark:text-white'>
                {employee?.name || 'غير محدد'}
              </p>
              <p className='truncate text-xs text-secondary-500'>
                {employee?.department || '-'}
              </p>
            </div>
          );
        },
      },
      {
        key: 'date',
        header: 'التاريخ',
        cell: record => formatDate(record.date),
      },
      {
        key: 'time',
        header: 'الحضور والانصراف',
        cell: record => (
          <span className='font-mono'>
            {formatTime(record.checkIn)} - {formatTime(record.checkOut)}
          </span>
        ),
      },
      {
        key: 'hours',
        header: 'الساعات',
        hideOnMobile: true,
        cell: record => formatDuration(record.totalHours),
      },
      {
        key: 'status',
        header: 'الحالة',
        cell: record => (
          <StatusBadge tone={statusTone[record.status]}>
            {statusLabels[record.status]}
          </StatusBadge>
        ),
      },
      {
        key: 'notes',
        header: 'ملاحظات',
        hideOnMobile: true,
        cell: record => record.notes || '-',
      },
    ],
    [employeeById]
  );

  const openManualModal = () => {
    setManualForm(form => ({
      ...form,
      employeeId: activeEmployees[0]?.id || '',
      date: filters.startDate,
    }));
    setManualOpen(true);
  };

  const getTimestampForTime = (date: string, time: string) =>
    Timestamp.fromDate(parseDateTimeInput(date, time));

  const inferStatus = (checkIn: string, status: Attendance['status']) => {
    if (status !== 'present') return status;
    const expected = parseDateTimeInput(manualForm.date, settings.workStartTime);
    expected.setMinutes(expected.getMinutes() + settings.lateThreshold);
    const actual = parseDateTimeInput(manualForm.date, checkIn);
    return actual > expected ? 'late' : 'present';
  };

  const submitManualAttendance = async () => {
    if (!manualForm.employeeId) {
      toast.error('اختر الموظف أولًا');
      return;
    }
    setSavingManual(true);
    try {
      const hasTimes = manualForm.status === 'present';
      await recordManualAttendance({
        employeeId: manualForm.employeeId,
        date: Timestamp.fromDate(parseDateInput(manualForm.date)),
        checkIn: hasTimes
          ? getTimestampForTime(manualForm.date, manualForm.checkIn)
          : undefined,
        checkOut:
          hasTimes && manualForm.checkOut
            ? getTimestampForTime(manualForm.date, manualForm.checkOut)
            : undefined,
        status: hasTimes
          ? inferStatus(manualForm.checkIn, manualForm.status)
          : manualForm.status,
        notes: manualForm.notes.trim() || undefined,
      });
      toast.success('تم تسجيل الحضور');
      setManualOpen(false);
      await loadAttendance();
    } catch (manualError) {
      toast.error(
        manualError instanceof Error
          ? manualError.message
          : 'تعذر تسجيل الحضور'
      );
    } finally {
      setSavingManual(false);
    }
  };

  if (employeesLoading && !employees.length) {
    return (
      <Surface className='flex min-h-[24rem] items-center justify-center'>
        <LoadingSpinner size='large' />
      </Surface>
    );
  }

  return (
    <div className='space-y-6'>
      <PageHeader
        eyebrow='الموارد البشرية'
        title='إدارة الحضور والانصراف'
        description='متابعة حضور الموظفين من بيانات Firestore الفعلية مع تسجيل يدوي عند الحاجة.'
        actions={
          <>
            <Button
              variant='secondary'
              onClick={() => {
                void fetchEmployees();
                void loadAttendance();
              }}
              leadingIcon={<RefreshCw className='h-4 w-4' />}
            >
              تحديث
            </Button>
            <Button
              onClick={openManualModal}
              disabled={!activeEmployees.length}
              leadingIcon={<ClipboardCheck className='h-4 w-4' />}
            >
              تسجيل يدوي
            </Button>
          </>
        }
      />

      {error ? (
        <Surface>
          <EmptyState
            icon={AlertTriangle}
            title='تعذر تحميل الموظفين'
            description={error}
          />
        </Surface>
      ) : null}

      <div className='grid grid-cols-2 gap-3 xl:grid-cols-4'>
        <MetricCard label='الحضور' value={stats.present} icon={UserCheck} tone='green' />
        <MetricCard label='الغياب' value={stats.absent} icon={UserX} tone='red' />
        <MetricCard label='التأخير' value={stats.late} icon={Clock} tone='amber' />
        <MetricCard
          label='معدل الحضور'
          value={`${stats.rate}%`}
          icon={CalendarDays}
          note={`إجمالي الساعات: ${formatDuration(stats.totalHours)}`}
        />
      </div>

      <Surface>
        <div className='grid gap-4 md:grid-cols-5'>
          <Input
            label='من تاريخ'
            type='date'
            value={filters.startDate}
            onChange={event =>
              setFilters({ ...filters, startDate: event.target.value })
            }
          />
          <Input
            label='إلى تاريخ'
            type='date'
            value={filters.endDate}
            onChange={event =>
              setFilters({ ...filters, endDate: event.target.value })
            }
          />
          <label className='space-y-2 text-sm font-semibold text-secondary-700 dark:text-secondary-200'>
            <span>الموظف</span>
            <select
              value={filters.employeeId}
              onChange={event =>
                setFilters({ ...filters, employeeId: event.target.value })
              }
              className='min-h-10 w-full rounded-lg border border-secondary-300 bg-white px-3 text-sm dark:border-secondary-700 dark:bg-secondary-800 dark:text-white'
            >
              <option value='all'>كل الموظفين</option>
              {activeEmployees.map(employee => (
                <option key={employee.id} value={employee.id}>
                  {employee.name}
                </option>
              ))}
            </select>
          </label>
          <label className='space-y-2 text-sm font-semibold text-secondary-700 dark:text-secondary-200'>
            <span>القسم</span>
            <select
              value={filters.department}
              onChange={event =>
                setFilters({ ...filters, department: event.target.value })
              }
              className='min-h-10 w-full rounded-lg border border-secondary-300 bg-white px-3 text-sm dark:border-secondary-700 dark:bg-secondary-800 dark:text-white'
            >
              <option value='all'>كل الأقسام</option>
              {departments.map(department => (
                <option key={department} value={department}>
                  {department}
                </option>
              ))}
            </select>
          </label>
          <label className='space-y-2 text-sm font-semibold text-secondary-700 dark:text-secondary-200'>
            <span>الحالة</span>
            <select
              value={filters.status}
              onChange={event =>
                setFilters({
                  ...filters,
                  status: event.target.value as StatusFilter,
                })
              }
              className='min-h-10 w-full rounded-lg border border-secondary-300 bg-white px-3 text-sm dark:border-secondary-700 dark:bg-secondary-800 dark:text-white'
            >
              <option value='all'>كل الحالات</option>
              {Object.entries(statusLabels).map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
          </label>
        </div>
      </Surface>

      <Surface padding='none'>
        {attendanceLoading ? (
          <div className='flex min-h-[18rem] items-center justify-center'>
            <LoadingSpinner size='large' />
          </div>
        ) : (
          <DataTable
            caption='سجلات حضور الموظفين'
            columns={columns}
            rows={filteredRecords}
            rowKey={record => record.id}
            emptyState={
              <EmptyState
                icon={ClipboardCheck}
                title='لا توجد سجلات حضور'
                description='غيّر الفلاتر أو سجّل حضورًا يدويًا للموظفين.'
              />
            }
          />
        )}
      </Surface>

      <Modal
        isOpen={manualOpen}
        onClose={() => {
          if (!savingManual) setManualOpen(false);
        }}
        title='تسجيل حضور يدوي'
        description='يسجل أو يحدث سجل حضور الموظف في اليوم المحدد.'
        footer={
          <>
            <Button
              variant='secondary'
              onClick={() => setManualOpen(false)}
              disabled={savingManual}
            >
              إلغاء
            </Button>
            <Button onClick={submitManualAttendance} loading={savingManual}>
              حفظ الحضور
            </Button>
          </>
        }
      >
        <div className='grid gap-4 sm:grid-cols-2'>
          <label className='space-y-2 text-sm font-semibold text-secondary-700 dark:text-secondary-200 sm:col-span-2'>
            <span>الموظف</span>
            <select
              value={manualForm.employeeId}
              disabled={savingManual}
              onChange={event =>
                setManualForm({ ...manualForm, employeeId: event.target.value })
              }
              className='min-h-10 w-full rounded-lg border border-secondary-300 bg-white px-3 text-sm disabled:cursor-not-allowed disabled:bg-secondary-100 disabled:text-secondary-500 dark:border-secondary-700 dark:bg-secondary-800 dark:text-white'
            >
              {activeEmployees.map(employee => (
                <option key={employee.id} value={employee.id}>
                  {employee.name}
                </option>
              ))}
            </select>
          </label>
          <Input
            label='التاريخ'
            type='date'
            value={manualForm.date}
            disabled={savingManual}
            onChange={event =>
              setManualForm({ ...manualForm, date: event.target.value })
            }
          />
          <label className='space-y-2 text-sm font-semibold text-secondary-700 dark:text-secondary-200'>
            <span>الحالة</span>
            <select
              value={manualForm.status}
              disabled={savingManual}
              onChange={event =>
                setManualForm({
                  ...manualForm,
                  status: event.target.value as Attendance['status'],
                })
              }
              className='min-h-10 w-full rounded-lg border border-secondary-300 bg-white px-3 text-sm disabled:cursor-not-allowed disabled:bg-secondary-100 disabled:text-secondary-500 dark:border-secondary-700 dark:bg-secondary-800 dark:text-white'
            >
              {Object.entries(statusLabels).map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
          </label>
          <Input
            label='وقت الحضور'
            type='time'
            value={manualForm.checkIn}
            disabled={savingManual || manualForm.status !== 'present'}
            onChange={event =>
              setManualForm({ ...manualForm, checkIn: event.target.value })
            }
          />
          <Input
            label='وقت الانصراف'
            type='time'
            value={manualForm.checkOut}
            disabled={savingManual || manualForm.status !== 'present'}
            onChange={event =>
              setManualForm({ ...manualForm, checkOut: event.target.value })
            }
          />
          <label className='space-y-2 text-sm font-semibold text-secondary-700 dark:text-secondary-200 sm:col-span-2'>
            <span>ملاحظات</span>
            <textarea
              value={manualForm.notes}
              disabled={savingManual}
              onChange={event =>
                setManualForm({ ...manualForm, notes: event.target.value })
              }
              rows={3}
              className='w-full rounded-lg border border-secondary-300 bg-white px-3 py-2 text-sm disabled:cursor-not-allowed disabled:bg-secondary-100 disabled:text-secondary-500 dark:border-secondary-700 dark:bg-secondary-800 dark:text-white'
            />
          </label>
        </div>
      </Modal>
    </div>
  );
};

export default AttendanceManagement;
